"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { getZoneById } from "@/lib/zones"
import type { DailyEntry } from "@/lib/db/schema"
import { Loader2 } from "lucide-react"

interface DeleteEntryDialogProps {
  entry: DailyEntry
  open: boolean
  onOpenChange: (open: boolean) => void
  onDelete: (entryId: string) => Promise<void>
}

export function DeleteEntryDialog({
  entry,
  open,
  onOpenChange,
  onDelete,
}: DeleteEntryDialogProps) {
  const [deleting, setDeleting] = useState(false)
  const zone = getZoneById(entry.zoneId)

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await onDelete(entry.id)
      onOpenChange(false)
    } catch (error) {
      console.error("Error deleting entry:", error)
    } finally {
      setDeleting(false)
    }
  }

  const formattedDate = new Date(entry.date).toLocaleDateString("cs-CZ", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Smazat záznam?</AlertDialogTitle>
          <AlertDialogDescription>
            Opravdu chceš smazat záznam ze dne <strong>{formattedDate}</strong>?
            {zone && (
              <span className="block mt-2">
                Zóna: {zone.emoji} {zone.name}
              </span>
            )}
            {entry.note && (
              <span className="block mt-2 text-xs italic">
                Poznámka: "{entry.note.slice(0, 100)}
                {entry.note.length > 100 ? "..." : ""}"
              </span>
            )}
            <span className="block mt-4 text-destructive">
              Tuto akci nelze vrátit zpět.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Zrušit</AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Mažu...
              </>
            ) : (
              "Smazat"
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
